import { Dispatch, State } from '../use_candidate_profile';
import {
  ApprovalNote,
  CandidateOffers,
  Offer as OfferData,
  OfferVersion,
} from '../../offers/types';
import { Offer } from '../../shared/types';
import {
  standardRequest,
  standardRequestWithErrorFlash,
} from '../../../shared/utils/request';
import rollbar from 'shared/utils/rollbar_logger';

type SetOfferVersions = {
  type: 'setOfferVersions';
  payload: { offerVersions: Array<OfferVersion> };
};
export const setOfferVersions =
  (state: State, dispatch: Dispatch) =>
  (offerVersions: Array<OfferVersion>) => {
    dispatch({
      type: 'setOfferVersions',
      payload: { offerVersions },
    });
  };

type SetOffer = {
  type: 'setOffer';
  payload: { offer: OfferData };
};

export const setOffer =
  (state: State, dispatch: Dispatch) => (offer: OfferData) => {
    dispatch({
      type: 'setOffer',
      payload: { offer },
    });
  };

type SetApprovalNotes = {
  type: 'setApprovalNotes';
  payload: { approvalNotes: ApprovalNote[] };
};

export const editApprovalNote =
  (state: State, dispatch: Dispatch) => (note: ApprovalNote) => {
    const approvalNotes = [...state.candidateOffers.approvalNotes].map((n) =>
      n.id === note.id ? note : n
    );

    dispatch({
      type: 'setApprovalNotes',
      payload: { approvalNotes },
    });
  };

export const deleteApprovalNote =
  (state: State, dispatch: Dispatch) => (noteId: number) => {
    const approvalNotes = [...state.candidateOffers.approvalNotes].filter(
      (note) => note.id !== noteId
    );
    dispatch({
      type: 'setApprovalNotes',
      payload: { approvalNotes },
    });
  };

export const setApprovalNotes =
  (_: State, dispatch: Dispatch) => (approvalNotes: Array<ApprovalNote>) => {
    dispatch({
      type: 'setApprovalNotes',
      payload: { approvalNotes },
    });
  };

type RefreshAllOffers = {
  type: 'refreshAllOffers';
};

type SetCandidateOffers = {
  type: 'setCandidateOffers';
  payload: {
    candidateOffers: Pick<
      CandidateOffers,
      'offerVersions' | 'approvalNotes' | 'offers'
    >;
  };
};

export const refreshAllOffers =
  (state: State, dispatch: Dispatch) => async () => {
    dispatch({
      type: 'setStatus',
      payload: { type: 'loading', key: 'offer' },
    });
    try {
      const response = await standardRequest<CandidateOffers>(
        state.paths.offers.offerDetailsPath
      );

      const newVersions = response.data.offerVersions;
      const oldVersions = state.candidateOffers.offerVersions;

      // if there are new offer versions, set the expanded offer to the most recent offer
      if (oldVersions.length !== newVersions.length) {
        dispatch({
          type: 'setExpandedOffer',
          payload: newVersions[newVersions.length - 1]?.id ?? null,
        });
      }

      dispatch({
        type: 'setCandidateOffers',
        payload: { candidateOffers: { ...response.data, offers: [] } },
      });
      dispatch({
        type: 'setHasOutstandingOffers',
        payload:
          response.data.offerVersions.length > 0 &&
          state?.decision?.status === 'in_process',
      });
      dispatch({
        type: 'setStatus',
        payload: { key: 'offer', type: 'complete' },
      });
    } catch (e: any) {
      dispatch({
        type: 'setStatus',
        payload: { type: 'error', key: 'offer', context: e },
      });
    }
  };

type RefreshSingleOffer = {
  type: 'refreshSingleOffer';
  payload: { id: number };
};

type SetSingleOfferLoading = {
  type: 'setSingleOfferLoading';
  payload: { id: number };
};

export const refreshSingleOffer =
  (state: State, dispatch: Dispatch) =>
  async (id: number, showLoadingState = true) => {
    const offerVersionToRefresh = state.candidateOffers.offerVersions.find(
      (v) => v.id === id
    );

    if (!offerVersionToRefresh) {
      const error = new Error(`Offer with id ${id} not found`);
      dispatch({
        type: 'setStatus',
        payload: { key: 'offer', type: 'error', context: error },
      });
      throw error;
    }

    try {
      if (showLoadingState) {
        dispatch({
          type: 'setSingleOfferLoading',
          payload: { id },
        });
      }

      const response = await standardRequest<OfferData>(
        offerVersionToRefresh.offerPath
      );

      dispatch({ type: 'setOffer', payload: { offer: response.data } });
    } catch (e) {
      dispatch({
        type: 'setStatus',
        payload: { key: 'offer', type: 'error', context: e },
      });
    }
  };

export const removeOutdatedApprovalWarning =
  (state: State, dispatch: Dispatch) => () => {
    if (!state.candidateOffers.showOutdatedApprovalWarning) {
      return;
    }
    dispatch({
      type: 'setShowOutdatedApprovalWarning',
      payload: false,
    });
  };

type SetHasOutstandingOffers = {
  type: 'setHasOutstandingOffers';
  payload: boolean;
};

type SetShowOutdatedApprovalWarning = {
  type: 'setShowOutdatedApprovalWarning';
  payload: boolean;
};

type SetExpandedOffer = {
  type: 'setExpandedOffer';
  payload: number | null;
};

export const setExpandedOffer =
  ({ expandedOffer }: State, dispatch: Dispatch) =>
  (id: number | null) => {
    if (expandedOffer === id) {
      dispatch({
        type: 'setExpandedOffer',
        payload: null,
      });

      return;
    }

    dispatch({
      type: 'setExpandedOffer',
      payload: id,
    });
  };

type GetOfferDetails = {
  type: 'getOfferDetails';
  payload: {
    offer: Offer;
  };
};

export const getOfferDetails =
  (state: State, dispatch: Dispatch) => async () => {
    try {
      const {
        data: { offer },
      } = await standardRequestWithErrorFlash.get(
        state.paths.offers.createOfferModalPath
      );

      dispatch({ type: 'getOfferDetails', payload: { offer } });
    } catch (e: unknown) {
      rollbar.error('CanPro: Error getting offer', {
        error: e,
        path: state.paths.offers.createOfferModalPath,
      });
    }
  };

export type Actions =
  | SetOfferVersions
  | SetOffer
  | SetApprovalNotes
  | RefreshAllOffers
  | SetCandidateOffers
  | RefreshSingleOffer
  | SetSingleOfferLoading
  | SetHasOutstandingOffers
  | SetShowOutdatedApprovalWarning
  | SetExpandedOffer
  | GetOfferDetails;
